import {
  constructSumTree,
  getSumTreeMaxIndexLessThan,
  getSumTreeSum,
  incrementSumTree,
  PartialSumTreeNode,
} from './SumTreeUtils';

/**
 * Manages a tree for quickly computing the n'th partial sum of a changing array: $\Sum_{k=0}^n a_k$
 *
 */

export type SummingOptions = {
  readonly maximumLength: number;
  readonly defaultValue: number;
};

export class PartialSummer {
  private sumTree: PartialSumTreeNode;
  private directAccessArray: number[];
  public constructor(private options: SummingOptions) {
    this.directAccessArray = Array(...new Array(this.options.maximumLength)).map(() => this.options.defaultValue);
    this.sumTree = constructSumTree(this.directAccessArray.map((v, i) => ({ sum: v, minIndex: i })));
  }

  /**
   * O(log(n))
   */
  public setIndexToValue = (index: number, value: number): void => {
    const delta = value - this.getValueForIndex(index);
    if (delta === 0) { return; }
    this.sumTree = incrementSumTree(this.sumTree, index, delta);
    this.directAccessArray[index] = value;
  };

  /**
   * O(1)
   */
  public getValueForIndex = (index: number): number => {
    return this.directAccessArray[index];
  };

  /**
   * O(log(n))
   */
  public getSumToIndex = (index: number): number => {
    return getSumTreeSum(this.sumTree, index);
  };

  /**
   * O(1)
   */
  public getTotalSum = (): number => {
    return this.sumTree.sum;
  };

  /**
   * "inverse" of getValueForIndex. For all X, returns the largest value n s.t. $\Sum_{k=0}^n a_k < X$
   * Correctness guaranteed iff all values > 0
   * O(log(n))
   */
  public getIndexForSum = (value: number): number => {
    return getSumTreeMaxIndexLessThan(this.sumTree, value);
  };
}
